import { Link, useLocation } from 'react-router-dom';
import { NavIcon } from '../NavIcon.tsx';
import { activeTab, type MobileTab } from '../../lib/mobileNav.ts';

/**
 * Tailwind only emits a class it can find as a literal string in the source, so
 * the breakpoint cannot be interpolated into a template — `${bp}:hidden` compiles
 * to no CSS at all and the bar would show at every width. Both literals live
 * here instead.
 */
const HIDE_ABOVE: Record<'md' | 'lg', string> = { md: 'md:hidden', lg: 'lg:hidden' };

type BottomTabBarProps = {
  /** Ordered tabs for this app — five for admin staff, four for a driver. */
  tabs: MobileTab[];
  /**
   * The width at which the desktop chrome takes over. The admin app hands its
   * 260px sidebar back at `lg`; the driver portal, which has no sidebar and only
   * four tabs, switches at `md`.
   */
  hideAt: 'md' | 'lg';
  /**
   * Counts to badge, by tab key. Deliberately a prop rather than a query in
   * here: this component is shared with the driver portal, which has no queue and
   * must not fetch an admin endpoint to find that out.
   *
   * A stale or failed count is worse than none on a nav badge — it would send
   * someone into an empty review queue — so the caller passes a key only for a
   * count it actually knows, and this renders a badge only above zero.
   *
   * A tab may also carry its own `badge`; it wins over this record.
   */
  badges?: Record<string, number>;
};

/**
 * The phone/tablet-portrait primary navigation, replacing the desktop shell's
 * sidebar or tab row. Rendered as the last child of the shell's flex column and
 * `shrink-0`, deliberately NOT `position: fixed`: the scroll container is the
 * shell's <main>, so a fixed bar would need matching bottom padding on every
 * screen and would still jump around while iOS Safari's URL bar collapses.
 *
 * Hidden above `hideAt` by the component itself so the shells only have to render
 * it unconditionally — see MobileAppBar, which does the same.
 */
export function BottomTabBar({ tabs, hideAt, badges }: BottomTabBarProps) {
  const location = useLocation();
  const current = activeTab(location.pathname, tabs);

  return (
    <nav
      aria-label="Primary"
      className={`flex shrink-0 items-stretch border-t border-slate-200 bg-white/[0.96] px-1 pt-1.5 backdrop-blur-md ${HIDE_ABOVE[hideAt]}`}
      // The home indicator overlaps the bottom of the viewport on notched
      // phones; 6px is the design's padding everywhere else.
      style={{ paddingBottom: 'max(6px, env(safe-area-inset-bottom))' }}
    >
      {tabs.map((tab) => {
        const isActive = current?.key === tab.key;
        const badgeCount = tab.badge ?? badges?.[tab.key] ?? 0;
        return (
          <Link
            key={tab.key}
            to={tab.to}
            // NavLink can't drive this highlight on its own: `activeTab` also
            // resolves ties between tabs whose paths nest, and can honour a tab's
            // `extraPrefixes`. One source of truth beats two disagreeing ones.
            {...(isActive ? { 'aria-current': 'page' as const } : {})}
            // `relative` anchors the label's sr-only count to this tab, so the
            // 1px absolutely-positioned node can never land outside the bar.
            className={`relative flex min-h-[52px] flex-1 flex-col items-center justify-center gap-1 py-1.5 ${
              // text-brand-active, not text-brand: the raw primary is whatever
              // the tenant saved (#fff is valid), while --brand-active-text is
              // brandTints' contrast-clamped variant. Identical for the default.
              isActive ? 'text-brand-active' : 'text-slate-400'
            }`}
          >
            <span className="relative flex items-center justify-center">
              <NavIcon name={tab.icon} size={24} />
              {badgeCount > 0 && (
                // Visual only. An aria-label on a role-less <span> is not
                // reliably announced; the count is spoken as visually-hidden
                // text AFTER the label instead, so the link reads
                // "Questionnaire (3 items need your action)".
                <span
                  className="absolute -top-1.5 left-3 flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
                  aria-hidden="true"
                >
                  {badgeCount}
                </span>
              )}
            </span>
            <span className="text-[10.5px] font-semibold tracking-[0.01em]">
              {tab.label}
              {badgeCount > 0 && (
                <span className="sr-only">
                  {' '}
                  ({badgeCount} {tab.badgeLabel ?? 'awaiting review'})
                </span>
              )}
            </span>
          </Link>
        );
      })}
    </nav>
  );
}
